import React, {MutableRefObject, useMemo} from 'react';

import {Folder} from '../../../types';

import {View, TextInput as TextInputType} from 'react-native';
import {defaultFolders, recursiveShare} from '../../../api';
import {
  ActivityIndicator,
  Divider,
  List,
  Text,
  useTheme,
  TextInput,
  IconButton,
  Menu,
} from 'react-native-paper';
import {TouchableOpacity} from 'react-native-gesture-handler';
import {useAppDispatch, useAppSelector} from '../../../store/hooks';
import {
  setFolderMenuItemId,
  setToastText,
} from '../../../store/slices/ui';
import {setEditingFolderName} from '../../../store/slices/folders';

type FolderItemCallback = (
  i: Folder,
  isSecondLevel?: boolean,
) => void;

export interface FolderItemProps {
  folder: Folder;
  enableMove?: boolean;
  currentFolderEditorName?: string;
  styles: any;
  folderNameInputRef?: MutableRefObject<TextInputType | null>;
  setFolderEditorName?: any;
  iconColor: string;
  iconSize: number;
  onFolderPress: FolderItemCallback;
  onFolderActionPress?: FolderItemCallback;
  onRemoveFolder?: FolderItemCallback;
  onMoveFolder?: FolderItemCallback;
  onShareFolder?: FolderItemCallback;
  abortEditFolder?: FolderItemCallback;
  width: number;
  isSecondLevel?: boolean;
  children?: React.ReactNode;
  isOpen?: boolean;
  isLoadingContent?: boolean;
  onDragEnd?: () => void;
  onDragStart?: () => void;
  showMenu?: () => void;
  hideMenu?: () => void;
}

const FolderItem = (props: FolderItemProps) => {
  const {
    folder,
    width,
    onFolderPress,
    onRemoveFolder,
    onFolderActionPress,
    isSecondLevel,
    children,
    isOpen,
    isLoadingContent,
    folderNameInputRef,
    abortEditFolder,
    onShareFolder,
    onDragStart,
    onDragEnd,
    showMenu,
    hideMenu,
  } = props;
  const theme = useTheme();

  let folderIcon = folder.isOpen ? 'folder-open' : 'folder-outline';
  let folderAccesory = folder.isInfoOpen
    ? 'checkbox-outline'
    : 'pencil-outline';
  let isDefaultFolder = false;
  if (defaultFolders.includes(folder.id)) {
    isDefaultFolder = true;
    folderAccesory = 'star';
  }
  if (folder.mnemonic) {
    folderIcon = folder.isOpen
      ? 'folder-network'
      : 'folder-network-outline';
  }
  if (folder.isInfoOpen) {
    folderIcon = 'arrow-left-circle';
  }
  const touchableStyle = {
    pointerEvents: 'auto',
    padding: isOpen ? 5 : 0,
    paddingTop: 15,
  } as any;
  const dispatch = useAppDispatch();
  const shareAllMedia = () => {
    dispatch(setFolderMenuItemId({itemId: undefined}));
    recursiveShare(folder);
    dispatch(setToastText('All folder files have been shared'));
  };
  const currentFolderEditorDraft = useAppSelector(
    state => state.folders.isEditingFolderDraft[folder.id],
  );
  const isFolderEditing = useAppSelector(
    state => state.folders.isEditingFolder[folder.id],
  );
  const isMenuOpen =
    useAppSelector(state => state.ui.folderItemMenuOpen) ===
    folder.id;
  useMemo(() => {
    if (isFolderEditing)
      dispatch(setEditingFolderName({[folder.id]: folder.title}));
  }, [isFolderEditing]);

  const remove = async () => {
    if (onRemoveFolder) await onRemoveFolder(folder);
  };
  const edit = async () => {
    if (isDefaultFolder) return;

    if (onFolderActionPress)
      await onFolderActionPress(folder, isSecondLevel);
    setTimeout(() => {
      folderNameInputRef?.current?.focus();
    }, 300);
  };

  const leftItem = (props: any) =>
    isLoadingContent ? (
      <ActivityIndicator style={{paddingLeft: 20}} />
    ) : (
      <TouchableOpacity
        onPress={() => onFolderPress(folder, isSecondLevel)}
        style={touchableStyle}>
        <List.Icon {...props} icon={folderIcon} />
      </TouchableOpacity>
    );

  const rightItem = (props: any) => (
    <View style={{flexDirection: 'row', flexWrap: 'wrap'}}>
      {!isDefaultFolder && !folder.isInfoOpen ? (
        <>
          <TouchableOpacity
            onPressIn={onDragStart}
            onPressOut={onDragEnd}
            style={{pointerEvents: 'auto'}}>
            <IconButton icon={'dots-grid'} />
          </TouchableOpacity>
          <Menu
            anchor={
              <IconButton icon={'dots-vertical'} onPress={showMenu} />
            }
            style={{marginTop: 50}}
            visible={isMenuOpen}
            onDismiss={hideMenu}>
            <>
              <Menu.Item
                leadingIcon={'heart'}
                title={'Share All Media'}
                onPress={() => shareAllMedia()}
              />
              <Divider />
            </>
            <>
              <Menu.Item
                leadingIcon={'share'}
                title={'Share Folder'}
                onPress={() => onShareFolder && onShareFolder(folder)}
              />
              <Divider />
            </>
            <>
              <Menu.Item
                leadingIcon={folderAccesory}
                title={'Edit Folder'}
                onPress={edit}
              />
              <Divider />
            </>
            <>
              <Menu.Item
                leadingIcon={'delete'}
                title={'Delete Folder'}
                onPress={remove}
              />
            </>
          </Menu>
        </>
      ) : null}
    </View>
  );
  return (
    <>
      <Divider />
      {!children ? (
        <List.Item
          style={{width}}
          title={({ellipsizeMode, color: titleColor, fontSize}) => (
            <>
              {isFolderEditing ? (
                <TextInput
                  mode="outlined"
                  style={
                    {
                      height: 50,
                      width: '100%',
                      pointerEvents: 'auto',
                    } as any
                  }
                  placeholder={'new folder name'}
                  theme={theme}
                  value={currentFolderEditorDraft}
                  ref={folderNameInputRef}
                  onChangeText={text =>
                    dispatch(
                      setEditingFolderName({[folder.id]: text}),
                    )
                  }
                  left={
                    <TextInput.Icon
                      onPress={() =>
                        abortEditFolder && abortEditFolder(folder)
                      }
                      icon={folderIcon}
                    />
                  }
                  right={
                    <TextInput.Icon
                      onPress={edit}
                      icon={folderAccesory}
                    />
                  }
                  autoFocus
                  id="test-input"
                />
              ) : (
                <TouchableOpacity
                  style={{pointerEvents: 'auto'} as any}
                  onPress={() =>
                    onFolderPress(folder, isSecondLevel)
                  }>
                  <View style={{}}>
                    <Text
                      ellipsizeMode={ellipsizeMode}
                      style={{color: titleColor, fontSize}}
                      numberOfLines={2}>
                      {folder.title}
                    </Text>
                  </View>
                </TouchableOpacity>
              )}
            </>
          )}
          left={!isFolderEditing ? leftItem : () => <></>}
          right={rightItem}
          theme={theme}
        />
      ) : (
        <List.Accordion
          expanded={isOpen}
          title={folder.title}
          style={{width}}
          left={leftItem}
          right={() => <></>}
          onPress={() => onFolderPress(folder, isSecondLevel)}
          theme={theme}>
          {children}
        </List.Accordion>
      )}
      {/* <Divider/> */}
    </>
  );
};

export default FolderItem;
